import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";

import { viewWalletTransactions } from "src/api/wallet";
import CustomDateRange from "src/components/CustomDateRange";
import WalletDetails from "src/components/WalletDetails";
import { DataTable, PageHead, StatusBadge, type Column, type RowsPerPageOption } from "src/components/common";
import { renderCurrency } from "src/lib/utils";
import type { WalletTransaction } from "src/types/wallet";

const formatDateTime = (value?: string) => {
	if (!value) return "—";
	const parsed = new Date(value);
	return Number.isNaN(parsed.getTime()) ? "—" : format(parsed, "PPp");
};

const Wallet = () => {
	const [searchParams, setSearchParams] = useSearchParams();

	const page = Number(searchParams.get("page") ?? 1);
	const limitParam = searchParams.get("limit") ?? "10";
	const limit: RowsPerPageOption = limitParam === "All" ? "All" : Number(limitParam);
	const startDate = searchParams.get("start_date");
	const endDate = searchParams.get("end_date");

	const { data, isLoading } = useQuery({
		queryKey: ["wallet-transactions", page, limit, startDate, endDate],
		queryFn: () => viewWalletTransactions({ page, limit: limit === "All" ? 9999 : limit, startDate, endDate }),
	});

	const list = data?.data;
	const transactions = list?.data ?? [];
	const total = list?.total ?? transactions.length;

	const columns: Column<WalletTransaction>[] = [
		{
			key: "type",
			header: "Type",
			primary: true,
			cell: (row) =>
				row.type === "DE" ? (
					<StatusBadge status="overdue" label="Debit" />
				) : (
					<StatusBadge status="paid" label="Credit" />
				),
		},
		{
			key: "amount",
			header: "Amount",
			cell: (row) => <span className="tw-font-semibold">{renderCurrency(row.amount ?? 0)}</span>,
		},
		{ key: "date", header: "Date", cell: (row) => formatDateTime(row.created_at) },
		{ key: "description", header: "Description", cell: (row) => row.description ?? "—" },
	];

	return (
		<div className="tw-space-y-5">
			<PageHead title="Wallet" subtitle="Fund your wallet to cover transaction fees and payouts." />

			<WalletDetails />

			<section>
				<div className="tw-mb-3 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
					<h3 className="tw-text-[15px] tw-font-bold tw-text-text">Wallet History</h3>
					<CustomDateRange />
				</div>

				<DataTable
					data={transactions}
					columns={columns}
					rowKey={(row, index) => row.uuid ?? row.id ?? index}
					loading={isLoading}
					totalRows={total}
					page={page}
					onPageChange={(next) => {
						const params = new URLSearchParams(searchParams);
						params.set("page", String(next));
						setSearchParams(params);
					}}
					rowsPerPage={limit}
					onRowsPerPageChange={(next) => {
						const params = new URLSearchParams(searchParams);
						params.set("limit", String(next));
						params.set("page", "1");
						setSearchParams(params);
					}}
					emptyTitle="No wallet activity yet"
					emptyDescription="Top-ups and transaction fees will appear here."
				/>
			</section>
		</div>
	);
};

export default Wallet;
